import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../App';
import { useI18n } from '../i18n/index.jsx';
import api from '../api';

const ROLE_LABELS = {
  owner: { icon: '👑', label: 'Владелец', color: '#f59e0b' },
  accountant: { icon: '📊', label: 'Бухгалтер', color: '#10b981' },
  manager: { icon: '📋', label: 'Менеджер', color: '#6366f1' },
  viewer: { icon: '👁️', label: 'Просмотр', color: '#6b7280' },
  superadmin: { icon: '⚡', label: 'Суперадмін', color: '#ef4444' },
};

export default function CompanyTeam() {
  const { user, setPage, currentCompany } = useContext(AppContext);
  const { t } = useI18n();

  const [members, setMembers] = useState([]);
  const [myRole, setMyRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', telegram_username: '', company_role: 'viewer' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, [currentCompany?.id]);

  async function load() {
    try {
      const [membersRes, roleRes] = await Promise.all([
        api.get('/company/team/members', { params: currentCompany?.id ? { company_id: currentCompany.id } : {} }),
        api.get('/company/team/my-role'),
      ]);
      setMembers(membersRes.data);
      setMyRole(roleRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite() {
    setInviteLoading(true);
    setError(null);
    try {
      await api.post('/company/team/invite', {
        email: inviteForm.email || null,
        telegram_username: inviteForm.telegram_username || null,
        company_role: inviteForm.company_role,
      });
      setShowInvite(false);
      setInviteForm({ email: '', telegram_username: '', company_role: 'viewer' });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка приглашения');
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleChangeRole(memberId, newRole) {
    try {
      await api.patch(`/company/team/members/${memberId}/role`, { company_role: newRole });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка');
    }
  }

  async function handleRemove(memberId) {
    if (!confirm('Удалить участника из команды?')) return;
    try {
      await api.delete(`/company/team/members/${memberId}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка');
    }
  }

  const canManage = myRole?.role === 'owner' || myRole?.role === 'superadmin';

  if (loading) return <div className="page"><div className="spinner" /></div>;

  return (
    <div className="page">
      <button className="btn btn-link back-btn" onClick={() => setPage('dashboard')}>← Назад</button>
      <h1>👥 Команда</h1>

      {error && <div className="banner banner-warning">⚠️ {error}</div>}

      {myRole && (
        <div style={{
          padding: 12, borderRadius: 10, marginBottom: 16,
          background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)',
          fontSize: 13
        }}>
          Ваша роль: <strong>{ROLE_LABELS[myRole.role]?.icon} {ROLE_LABELS[myRole.role]?.label || myRole.role}</strong>
        </div>
      )}

      {canManage && (
        <button
          className="btn btn-primary"
          style={{ marginBottom: 16 }}
          onClick={() => setShowInvite(!showInvite)}
        >
          + Пригласить участника
        </button>
      )}

      {/* Invite Form */}
      {showInvite && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <h3>📨 Пригласить</h3>
          <div className="form">
            <div className="field">
              <label>{t('common.email')}</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))}
                placeholder="user@example.com"
              />
            </div>
            <div className="field">
              <label>Telegram username</label>
              <input
                type="text"
                value={inviteForm.telegram_username}
                onChange={e => setInviteForm(p => ({ ...p, telegram_username: e.target.value }))}
                placeholder="@username"
              />
            </div>
            <div className="field">
              <label>Роль</label>
              <select
                value={inviteForm.company_role}
                onChange={e => setInviteForm(p => ({ ...p, company_role: e.target.value }))}
              >
                <option value="viewer">👁️ Просмотр</option>
                <option value="manager">📋 Менеджер</option>
                <option value="accountant">📊 Бухгалтер</option>
                <option value="owner">👑 Владелец</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={handleInvite} disabled={inviteLoading}>
              {inviteLoading ? '⏳...' : '📨 Отправить'}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowInvite(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="list">
        {members.map(m => {
          const roleInfo = ROLE_LABELS[m.company_role] || { icon: '?', label: m.company_role, color: '#888' };
          const isFounder = m.is_founder;
          return (
            <div key={m.id || m.user_id} className="list-item" style={{ alignItems: 'flex-start' }}>
              <div className="list-main">
                <div className="list-title">
                  {m.first_name || '?'} {m.last_name || ''}
                  {isFounder && <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 6 }}>основатель</span>}
                </div>
                <div className="list-sub">
                  {m.email && <span>📧 {m.email}</span>}
                  {m.telegram_username && <span style={{ marginLeft: 8 }}>@{m.telegram_username}</span>}
                </div>
                {m.permissions && (
                  <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {m.permissions.slice(0, 5).map(p => (
                      <span key={p} style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                        background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)'
                      }}>{p}</span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                <span style={{
                  padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  background: `${roleInfo.color}22`, color: roleInfo.color,
                  border: `1px solid ${roleInfo.color}44`,
                }}>
                  {roleInfo.icon} {roleInfo.label}
                </span>
                {canManage && !isFounder && m.id > 0 && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <select
                      value={m.company_role}
                      onChange={e => handleChangeRole(m.id, e.target.value)}
                      style={{ fontSize: 11, padding: '2px 4px', borderRadius: 4 }}
                    >
                      <option value="viewer">Просмотр</option>
                      <option value="manager">Менеджер</option>
                      <option value="accountant">Бухгалтер</option>
                      <option value="owner">Владелец</option>
                    </select>
                    <button
                      style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
                        background: 'rgba(239,68,68,0.2)', color: '#f87171', border: 'none'
                      }}
                      onClick={() => handleRemove(m.id)}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {members.length === 0 && (
          <div className="empty">Пока никого. Пригласите первого участника!</div>
        )}
      </div>
    </div>
  );
}
